import { supabase } from '../../../lib/supabase';
import { Appointment, AppointmentStatus, StaffMember } from '../types';
import { safeStorage } from '../../../utils/safeStorage';
import { DEMO_PRESETS } from '../components/DirectBookingPage';
import { getLocalDateString } from '../utils/calendarUtils';

export interface StaffPortalPayload {
  staff: StaffMember;
  businessName: string;
  businessSlug?: string;
  businessPhone?: string;
  appointments: Appointment[];
  services: Array<{ id: string; name: string; price: number; durationMinutes: number; category?: string }>;
  isDemo?: boolean;
  isOfflineFallback?: boolean;
}

// Chave para cache local / offline
const STAFF_PORTAL_LOCAL_STORAGE_KEY = 'rmax_staff_portal_cache_';
const LOCAL_STAFF_MEMBERS_KEY = 'rmax_local_staff_members';

export async function getStaffPortalData(token: string): Promise<StaffPortalPayload | null> {
  const cleanToken = (token || '').trim();
  if (!cleanToken) return null;

  // 1. Cenário Demo Padrão para Testes Imediatos
  if (cleanToken === 'demo' || cleanToken === 'demo-mariana' || cleanToken === 'mariana' || cleanToken === 'st_1') {
    const demoStaff: StaffMember = {
      id: 'st_1',
      name: 'Mariana Silva',
      role: 'Manicure & Alongamento em Gel',
      color: 'rose',
      commissionPercent: 50,
      phone: '11988887777',
      accessToken: 'demo-mariana',
      pinCode: '1234',
      active: true,
      permissions: {
        canCreateAppointments: true,
        canBlockSlots: true,
        canComplete: true,
        showClientPhone: true,
        showCommission: true
      }
    };

    const todayStr = getLocalDateString();
    const demoAppointments: Appointment[] = [
      {
        id: 'appt_demo_1',
        userId: 'demo_user',
        clientName: 'Fernanda Lima',
        clientPhone: '11977776666',
        serviceName: 'Alongamento em Gel / Fibra',
        durationMinutes: 120,
        scheduledDate: todayStr,
        scheduledTime: '09:00',
        price: 160,
        status: 'confirmed',
        staffMemberId: 'st_1',
        staffMemberName: 'Mariana Silva',
        commissionPercent: 50,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      },
      {
        id: 'appt_demo_2',
        userId: 'demo_user',
        clientName: 'Beatriz Martins',
        clientPhone: '11988885555',
        serviceName: 'Manicure + Pedicure Tradicional',
        durationMinutes: 60,
        scheduledDate: todayStr,
        scheduledTime: '13:00',
        price: 65,
        status: 'completed',
        staffMemberId: 'st_1',
        staffMemberName: 'Mariana Silva',
        commissionPercent: 50,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      },
      {
        id: 'appt_demo_3',
        userId: 'demo_user',
        clientName: 'Almoço e Descanso',
        serviceName: 'Bloqueio / Intervalo',
        durationMinutes: 60,
        scheduledDate: todayStr,
        scheduledTime: '11:30',
        price: 0,
        status: 'blocked',
        staffMemberId: 'st_1',
        staffMemberName: 'Mariana Silva',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      }
    ];

    const cached = safeStorage.getItem(STAFF_PORTAL_LOCAL_STORAGE_KEY + cleanToken);
    const localAppts = cached ? JSON.parse(cached) : demoAppointments;

    // Verificar se há PIN customizado salvo localmente
    const localStaffListRaw = safeStorage.getItem(LOCAL_STAFF_MEMBERS_KEY);
    if (localStaffListRaw) {
      try {
        const list: StaffMember[] = JSON.parse(localStaffListRaw);
        const match = list.find(s => s.id === 'st_1' || s.accessToken === 'demo-mariana' || s.accessToken === cleanToken || s.id === cleanToken);
        if (match && match.pinCode) {
          demoStaff.pinCode = match.pinCode;
        }
      } catch (e) {
        // ignore
      }
    }

    return {
      staff: demoStaff,
      businessName: 'Studio Bella • Cabelo & Estética',
      businessSlug: 'studio-bella',
      businessPhone: '11999999999',
      appointments: localAppts,
      services: DEMO_PRESETS.estetica.services,
      isDemo: true
    };
  }

  // 2. Consulta no Supabase via RPC Segura (SECURITY DEFINER)
  try {
    const { data: rpcPayload, error: rpcError } = await supabase
      .rpc('rpc_get_staff_portal', { p_token: cleanToken });

    if (!rpcError && rpcPayload && rpcPayload.staff) {
      const payload = rpcPayload as StaffPortalPayload;

      // Enriquecimento Local: se rmax_data estiver no mesmo ambiente (gestor testando ou mesmo navegador),
      // garantir que agendamentos criados localmente que ainda não sincronizaram apareçam no portal
      const rmaxDataRaw = safeStorage.getItem('rmax_data');
      if (rmaxDataRaw) {
        try {
          const rmax = JSON.parse(rmaxDataRaw);
          if (Array.isArray(rmax.appointments)) {
            const localLinked = rmax.appointments.filter((a: any) => {
              if (!a) return false;
              const matchById = a.staffMemberId && (a.staffMemberId === payload.staff.id || a.staffMemberId === payload.staff.accessToken);
              const matchByName = Boolean(
                a.staffMemberName && 
                payload.staff.name && 
                a.staffMemberName.toLowerCase().trim() === payload.staff.name.toLowerCase().trim()
              );
              return matchById || matchByName;
            });
            if (localLinked.length > 0) {
              const apptMap = new Map<string, Appointment>();
              (payload.appointments || []).forEach(a => apptMap.set(a.id, a));
              localLinked.forEach(a => {
                if (!apptMap.has(a.id)) {
                  apptMap.set(a.id, a);
                } else {
                  apptMap.set(a.id, { ...apptMap.get(a.id)!, ...a });
                }
              });
              payload.appointments = Array.from(apptMap.values()).sort((a, b) => 
                (a.scheduledDate || '').localeCompare(b.scheduledDate || '') || 
                (a.scheduledTime || '').localeCompare(b.scheduledTime || '')
              );
            }
          }
          const realBizName = rmax.user?.businessName || rmax.storefrontProfile?.displayName;
          if (realBizName && (!payload.businessName || payload.businessName === 'Meu Estabelecimento')) {
            payload.businessName = realBizName;
          }
        } catch (e) {
          // ignore
        }
      }

      return payload;
    }
    if (rpcError) {
      console.warn('Erro ao consultar rpc_get_staff_portal:', rpcError);
    }
  } catch (err) {
    console.warn('RPC do Supabase indisponível, buscando no armazenamento local:', err);
  }

  // 3. Fallback Resiliente em Armazenamento Local / Memória
  const rmaxDataRaw = safeStorage.getItem('rmax_data');
  let rmaxData: any = null;
  if (rmaxDataRaw) {
    try {
      rmaxData = JSON.parse(rmaxDataRaw);
    } catch (e) {
      // ignore
    }
  }

  const localStaffListRaw = safeStorage.getItem(LOCAL_STAFF_MEMBERS_KEY);
  let localStaffList: StaffMember[] = [];
  if (localStaffListRaw) {
    try {
      localStaffList = JSON.parse(localStaffListRaw);
    } catch (e) {
      console.warn('Erro ao processar local staff:', e);
    }
  }

  const combinedStaffList: StaffMember[] = [
    ...(Array.isArray(rmaxData?.staffMembers) ? rmaxData.staffMembers : []),
    ...localStaffList
  ];

  const match = combinedStaffList.find(s => 
    s.accessToken === cleanToken || 
    s.id === cleanToken ||
    (s.name && s.name.toLowerCase().trim() === cleanToken.toLowerCase().trim()) ||
    (s.name && s.name.toLowerCase().replace(/\s+/g, '-') === cleanToken.toLowerCase().trim())
  );

  if (match) {
    const allSalonAppts: Appointment[] = Array.isArray(rmaxData?.appointments) ? rmaxData.appointments : [];
    const linkedAppts = allSalonAppts.filter(a => {
      if (!a) return false;
      const matchById = a.staffMemberId && (a.staffMemberId === match.id || a.staffMemberId === match.accessToken);
      const matchByName = Boolean(
        a.staffMemberName && 
        match.name && 
        a.staffMemberName.toLowerCase().trim() === match.name.toLowerCase().trim()
      );
      return matchById || matchByName;
    });

    const cachedApptsRaw = safeStorage.getItem(STAFF_PORTAL_LOCAL_STORAGE_KEY + cleanToken) || 
                           safeStorage.getItem(STAFF_PORTAL_LOCAL_STORAGE_KEY + match.id);
    const cachedAppts: Appointment[] = cachedApptsRaw ? JSON.parse(cachedApptsRaw) : [];

    const apptMap = new Map<string, Appointment>();
    linkedAppts.forEach(a => apptMap.set(a.id, a));
    cachedAppts.forEach(a => {
      if (!apptMap.has(a.id)) {
        apptMap.set(a.id, a);
      } else {
        apptMap.set(a.id, { ...apptMap.get(a.id)!, ...a });
      }
    });

    const staffWithPin = {
      ...match,
      pinCode: match.pinCode || (match.phone ? match.phone.replace(/\D/g, '').slice(-4) : '1234')
    };

    const resolvedBusinessName = 
      rmaxData?.user?.businessName || 
      rmaxData?.storefrontProfile?.displayName || 
      rmaxData?.user?.ownerName || 
      'Salão';

    const resolvedBusinessSlug = rmaxData?.storefrontProfile?.slug || 'agenda';
    const resolvedBusinessPhone = rmaxData?.storefrontProfile?.whatsappNumber || match.phone || '11999999999';
    const resolvedServices = (Array.isArray(rmaxData?.services) && rmaxData.services.length > 0)
      ? rmaxData.services.map((s: any) => ({
          id: s.id,
          name: s.name,
          price: s.price || s.value || 0,
          durationMinutes: s.durationMinutes || s.duration_minutes || 60,
          category: s.category || 'Geral'
        }))
      : DEMO_PRESETS.estetica.services;

    return {
      staff: staffWithPin,
      businessName: resolvedBusinessName,
      businessSlug: resolvedBusinessSlug,
      businessPhone: resolvedBusinessPhone,
      appointments: Array.from(apptMap.values()).sort((a, b) => 
        (a.scheduledDate || '').localeCompare(b.scheduledDate || '') || 
        (a.scheduledTime || '').localeCompare(b.scheduledTime || '')
      ),
      services: resolvedServices,
      isDemo: false,
      isOfflineFallback: true
    };
  }

  // 4. Se for um token de teste criado pelo usuário no formulário
  const autoStaff: StaffMember = {
    id: cleanToken.startsWith('st_') ? cleanToken : `st_${cleanToken}`,
    name: cleanToken.replace(/[-_]/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
    role: 'Profissional Parceiro',
    color: 'emerald',
    commissionPercent: 50,
    accessToken: cleanToken,
    pinCode: '1234',
    active: true,
    permissions: {
      canCreateAppointments: true,
      canBlockSlots: true,
      canComplete: true,
      showClientPhone: true,
      showCommission: true
    }
  };

  const cached = safeStorage.getItem(STAFF_PORTAL_LOCAL_STORAGE_KEY + cleanToken);
  const localAppts: Appointment[] = cached ? JSON.parse(cached) : [];

  return {
    staff: autoStaff,
    businessName: 'Meu Estabelecimento',
    businessSlug: 'salao',
    businessPhone: '11999999999',
    appointments: localAppts,
    services: DEMO_PRESETS.estetica.services,
    isDemo: true,
    isOfflineFallback: true
  };
}

export async function updateStaffPin(token: string, newPin: string, isDemo?: boolean): Promise<boolean> {
  const cleanPin = (newPin || '').replace(/\D/g, '').slice(0, 6);
  if (!cleanPin || cleanPin.length < 4) return false;

  // Atualiza no cache local
  try {
    const localStaffListRaw = safeStorage.getItem(LOCAL_STAFF_MEMBERS_KEY);
    let list: StaffMember[] = localStaffListRaw ? JSON.parse(localStaffListRaw) : [];
    const exists = list.some(s => s.accessToken === token || s.id === token);
    if (exists) {
      list = list.map(s => (s.accessToken === token || s.id === token) ? { ...s, pinCode: cleanPin } : s);
    } else {
      list.push({
        id: token,
        name: 'Profissional',
        role: 'Atendente',
        color: 'rose',
        commissionPercent: 50,
        accessToken: token,
        pinCode: cleanPin,
        active: true
      });
    }
    safeStorage.setItem(LOCAL_STAFF_MEMBERS_KEY, JSON.stringify(list));
  } catch (e) {
    console.warn('Erro ao atualizar PIN no storage local:', e);
  }

  if (isDemo) return true;

  try {
    const { data: success, error } = await supabase
      .rpc('rpc_staff_update_pin', { p_token: token, p_new_pin: cleanPin });

    if (error) {
      console.warn('Erro ao atualizar PIN via RPC no Supabase:', error);
      return false;
    }
    return Boolean(success);
  } catch (err) {
    console.error('Falha de rede ao atualizar PIN:', err);
    return false;
  }
}

export async function saveStaffAppointment(
  token: string, 
  staff: StaffMember, 
  appointment: Partial<Appointment>, 
  isDemo?: boolean
): Promise<Appointment | null> {
  const newId = appointment.id || (typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : 'appt_' + Date.now());
  const fullAppointment: Appointment = {
    id: newId,
    userId: (staff as any).userId || 'staff_direct',
    clientName: appointment.clientName || 'Cliente Agendado',
    clientPhone: appointment.clientPhone || undefined,
    clientAddress: appointment.clientAddress || undefined,
    serviceName: appointment.serviceName || 'Atendimento',
    serviceId: appointment.serviceId || undefined,
    servicesList: appointment.servicesList || [],
    durationMinutes: appointment.durationMinutes || 60,
    scheduledDate: appointment.scheduledDate || getLocalDateString(),
    scheduledTime: (appointment.scheduledTime || '09:00').slice(0, 5),
    price: appointment.price !== undefined ? appointment.price : 0,
    status: appointment.status || 'confirmed',
    staffMemberId: staff.id,
    staffMemberName: staff.name,
    commissionPercent: staff.commissionPercent || 50,
    allDay: Boolean(appointment.allDay || (appointment.durationMinutes && appointment.durationMinutes >= 720)),
    notes: appointment.notes || undefined,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  };

  // Salva no storage local como rascunho
  const cached = safeStorage.getItem(STAFF_PORTAL_LOCAL_STORAGE_KEY + token);
  const list: Appointment[] = cached ? JSON.parse(cached) : [];
  const updated = [fullAppointment, ...list.filter(a => a.id !== fullAppointment.id)];
  safeStorage.setItem(STAFF_PORTAL_LOCAL_STORAGE_KEY + token, JSON.stringify(updated));

  // Atualiza também na base principal de rmax_data se presente no dispositivo
  const rmaxDataRaw = safeStorage.getItem('rmax_data');
  if (rmaxDataRaw) {
    try {
      const rmax = JSON.parse(rmaxDataRaw);
      if (Array.isArray(rmax.appointments)) {
        const existingIdx = rmax.appointments.findIndex((a: any) => a.id === fullAppointment.id);
        if (existingIdx >= 0) {
          rmax.appointments[existingIdx] = fullAppointment;
        } else {
          rmax.appointments.unshift(fullAppointment);
        }
        safeStorage.setItem('rmax_data', JSON.stringify(rmax));
      }
    } catch (e) {
      // ignore
    }
  }

  if (isDemo) {
    return fullAppointment;
  }

  try {
    const payload = {
      id: fullAppointment.id,
      clientName: fullAppointment.clientName,
      clientPhone: fullAppointment.clientPhone || null,
      serviceName: fullAppointment.serviceName,
      serviceId: fullAppointment.serviceId || null,
      durationMinutes: fullAppointment.durationMinutes,
      scheduledDate: fullAppointment.scheduledDate,
      scheduledTime: fullAppointment.scheduledTime.slice(0, 5),
      price: fullAppointment.price,
      status: fullAppointment.status,
      paymentStatus: fullAppointment.paymentStatus || 'pending',
      allDay: Boolean(fullAppointment.allDay),
      notes: fullAppointment.notes || null
    };

    const { data: savedData, error } = await supabase
      .rpc('rpc_staff_save_appointment', { p_token: token, p_appointment: payload });

    if (error) {
      console.error('Erro ao salvar agendamento via RPC no Supabase:', error);
      throw new Error(error.message || 'Erro ao sincronizar com o banco de dados');
    } else if (savedData && (savedData as any).id) {
      fullAppointment.id = (savedData as any).id;
      // Sincroniza o ID oficial do banco no storage local
      const syncedList = updated.map(a => a.id === newId ? { ...a, id: (savedData as any).id } : a);
      safeStorage.setItem(STAFF_PORTAL_LOCAL_STORAGE_KEY + token, JSON.stringify(syncedList));
    }
    return fullAppointment;
  } catch (err: any) {
    console.warn('Falha de rede ao salvar agendamento pelo portal via RPC (salvo com sucesso no cache local):', err);
    return fullAppointment;
  }
}

export async function updateStaffAppointmentStatus(
  token: string, 
  appointmentId: string, 
  status: AppointmentStatus, 
  isDemo?: boolean
): Promise<boolean> {
  const nowIso = new Date().toISOString();
  const cached = safeStorage.getItem(STAFF_PORTAL_LOCAL_STORAGE_KEY + token);
  if (cached) {
    const list: Appointment[] = JSON.parse(cached);
    const updated = list.map(a => a.id === appointmentId ? { 
      ...a, 
      status, 
      executedAt: status === 'completed' ? (a.executedAt || nowIso) : a.executedAt,
      updatedAt: nowIso 
    } : a);
    safeStorage.setItem(STAFF_PORTAL_LOCAL_STORAGE_KEY + token, JSON.stringify(updated));
  }

  // Atualiza também na base principal de rmax_data se presente no dispositivo
  const rmaxDataRaw = safeStorage.getItem('rmax_data');
  if (rmaxDataRaw) {
    try {
      const rmax = JSON.parse(rmaxDataRaw);
      if (Array.isArray(rmax.appointments)) {
        rmax.appointments = rmax.appointments.map((a: any) => a.id === appointmentId ? {
          ...a,
          status,
          executedAt: status === 'completed' ? (a.executedAt || nowIso) : a.executedAt,
          updatedAt: nowIso
        } : a);
        safeStorage.setItem('rmax_data', JSON.stringify(rmax));
      }
    } catch (e) {
      // ignore
    }
  }

  if (isDemo) {
    return true;
  }

  try {
    const { data: success, error } = await supabase
      .rpc('rpc_staff_update_appointment_status', { 
        p_token: token, 
        p_appointment_id: appointmentId, 
        p_status: status 
      });

    if (error) {
      console.warn('Erro ao atualizar status do agendamento via RPC:', error);
      return false;
    }
    return Boolean(success);
  } catch (err) {
    console.error('Falha de rede ao atualizar status via RPC:', err);
    return false;
  }
}
